/**
 * PanelDavetiModal — bir kişiyi SMS ile geçici kişi paneline davet etmek (backlog #647; backend #644 / #633; sayfa #646).
 *
 * Kişi FDS kullanıcısı değildir: SMS ile gelen bağlantı + geçici şifreyle küçük bir sayfada kendi belgelerini yükler. Burada:
 *   · NUMARA: telefon MASKELİ gösterilir. Kullanılabilir bir Türkiye cep numarası yoksa (boş, geçersiz, sabit hat, yabancı) SMS gidemeyeceği söylenir ve numara
 *     burada düzeltilebilir (künye PATCH'i); düzeltmeden sonra durum yeniden okunur.
 *   · DURUM çipi: Gönderildi · Açıldı · Aktif · Süresi doldu · Kilitli · İptal (davet yoksa çip yok), tarihlerle.
 *   · GÖNDER / YENİDEN GÖNDER / İPTAL: gönderme iki adımlıdır — SMS'in para harcadığı ve hangi (maskeli) numaraya gideceği yazılı bir onay sonrası yollanır; yeniden gönderme
 *     sunucunun beklemesini (resend_after_seconds, ya da 429'da retry_after_seconds) geri sayar. Yeni davet eskisini iptal eder.
 *   · Geçici panel anahtarı (sms.gecici_panel.etkin) kapalıysa (`enabled:false`) davet gitmez, ekran bunu söyler.
 * Yetki sunucuda denetlenir (personel ya da portal.personel.manage'li kendi firması); düğmeyi yalnızca çağıran yer gösterir.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useGeriSayim from '../Common/useGeriSayim';
import TelefonGirdisi from '../Common/TelefonGirdisi';
import { kunyeyiGonder } from '../../services/kisiKunyeGonder';
import { talepGonderildi } from '../../services/kisiDuzenlemeTalebiService';
import { kilitMetni } from './duzenlemeTalebi';
import {
  getPanelDaveti, ilkDavetGonderilebilir, iptalEdilebilir, revokePanelDaveti, sendPanelDaveti, yenidenGonderilebilir,
} from '../../services/panelDavetiService';
import { currentLocaleTag } from '../../i18n';

const RENK = { NONE: 'secondary', SENT: 'info', OPENED: 'primary', ACTIVE: 'success', EXPIRED: 'secondary', LOCKED: 'danger', REVOKED: 'warning' };
const tarih = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString(currentLocaleTag()) : '';
};

export default function PanelDavetiModal({ personId, adSoyad, onClose }) {
  const { t } = useTranslation('sms');
  const { t: tc } = useTranslation('components');
  const [davet, setDavet] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [adim, setAdim] = useState('ozet');            // 'ozet' | 'onay' | 'iptal'
  const [calisiyor, setCalisiyor] = useState(false);
  const [numara, setNumara] = useState('');
  const [numaraYazilir, setNumaraYazilir] = useState(false);
  const [kalan, geriSayimiBaslat] = useGeriSayim(0);

  const oku = useCallback(async () => {
    try {
      const d = await getPanelDaveti(personId);
      setDavet(d);
      if (d.resend_after_seconds > 0) geriSayimiBaslat(d.resend_after_seconds);
      setHata(null);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [personId, geriSayimiBaslat]);

  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const d = await getPanelDaveti(personId);
        if (iptal) return;
        setDavet(d);
        if (d.resend_after_seconds > 0) geriSayimiBaslat(d.resend_after_seconds);
      } catch (e) {
        if (!iptal) setHata(e.message);
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
  }, [personId, geriSayimiBaslat]);

  const sorunMetni = davet && davet.phone_problem ? t(`davet.numaraSorunu.${davet.phone_problem}`, { defaultValue: davet.phone_problem }) : null;
  const gonderilebilir = ilkDavetGonderilebilir(davet);
  const tekrar = yenidenGonderilebilir(davet);
  const iptalde = iptalEdilebilir(davet);

  async function gonder() {
    setCalisiyor(true);
    setHata(null);
    setMesaj(null);
    try {
      const d = await sendPanelDaveti(personId);
      setDavet(d);
      geriSayimiBaslat(d.resend_after_seconds > 0 ? d.resend_after_seconds : 60);
      setMesaj(t('davet.gonderildi'));
      setAdim('ozet');
    } catch (e) {
      if (e.status === 429 && e.data && e.data.retry_after_seconds) geriSayimiBaslat(e.data.retry_after_seconds);
      setHata(e.message);
      setAdim('ozet');
    } finally {
      setCalisiyor(false);
    }
  }

  async function iptalEt() {
    setCalisiyor(true);
    setHata(null);
    setMesaj(null);
    try {
      setDavet(await revokePanelDaveti(personId));
      setMesaj(t('davet.iptalEdildi'));
      setAdim('ozet');
    } catch (e) {
      setHata(e.message);
      setAdim('ozet');
    } finally {
      setCalisiyor(false);
    }
  }

  async function numarayiDuzelt(e) {
    e.preventDefault();
    if (!numara.trim()) return;
    setCalisiyor(true);
    setHata(null);
    setMesaj(null);
    try {
      const sonuc = await kunyeyiGonder(personId, { telefon: numara.trim() }); // a company's pending e-mail request rides along (#749)
      setNumara('');
      setNumaraYazilir(false);
      if (talepGonderildi(sonuc)) {
        // #749 (backend #747): a company login's change on a LOCKED card (KVKK / Kimlik / verified phone) is not written, it waits for SAKA's approval.
        setMesaj(t('davet.numaraTalepGonderildi', { sebepler: kilitMetni(tc, sonuc.kilitSebepleri) }));
      } else {
        setMesaj(t('davet.numaraKaydedildi'));
        await oku();
      }
    } catch (err) {
      setHata(err.message);
    } finally {
      setCalisiyor(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !calisiyor && onClose()} centered>
      <ModalHeader toggle={() => !calisiyor && onClose()}>{t('davet.baslik')}{adSoyad ? `: ${adSoyad}` : ''}</ModalHeader>
      <ModalBody data-testid="panel-daveti">
        {yukleniyor && <div className="text-center py-3"><Spinner /></div>}
        {hata && <Alert color="danger" fade={false} data-testid="panel-daveti-hata">{hata}</Alert>}
        {mesaj && <Alert color="success" fade={false} className="py-2" data-testid="panel-daveti-mesaj">{mesaj}</Alert>}
        {davet && (
          <>
            <p className="text-muted small">{t('davet.aciklama')}</p>
            {davet.enabled === false && <Alert color="secondary" fade={false} className="py-2" data-testid="panel-daveti-kapali">{t('davet.kapali')}</Alert>}

            <div className="mb-3">
              <div className="text-muted small">{t('davet.telefon')}</div>
              <div className="fw-semibold" data-testid="panel-daveti-telefon">{davet.phone_masked || <span className="text-muted fw-normal">—</span>}</div>
              {sorunMetni && (
                <Alert color="warning" fade={false} className="py-2 mt-2 mb-0" data-testid="panel-daveti-sorun">
                  <div>{sorunMetni}</div>
                  <div className="small mt-1">{t('davet.smsGidemez')}</div>
                </Alert>
              )}
              {!numaraYazilir ? (
                <Button color="link" size="sm" className="px-0" id="panel-daveti-numara-duzelt" onClick={() => setNumaraYazilir(true)}>
                  {sorunMetni ? t('davet.numaraYaz') : t('davet.numaraDegistir')}
                </Button>
              ) : (
                <Form onSubmit={numarayiDuzelt} className="mt-2" data-testid="panel-daveti-numara-formu">
                  <Label htmlFor="panel-daveti-numara" className="small mb-0">{t('davet.yeniNumara')}</Label>
                  <div className="d-flex gap-2">
                    <TelefonGirdisi id="panel-daveti-numara" className="flex-grow-1" value={numara} onChange={setNumara} />
                    <Button color="primary" type="submit" disabled={calisiyor || !numara.trim()} id="panel-daveti-numara-kaydet">{t('kaydet')}</Button>
                    <Button color="light" type="button" onClick={() => { setNumaraYazilir(false); setNumara(''); }}>{t('vazgec')}</Button>
                  </div>
                </Form>
              )}
            </div>

            <div className="mb-3" data-testid="panel-daveti-durum">
              <div className="text-muted small">{t('davet.durum')}</div>
              {davet.status === 'NONE'
                ? <span className="text-muted">{t('davet.yok')}</span>
                : <Badge color={RENK[davet.status] || 'secondary'} data-testid="panel-daveti-cip">{t(`davet.durumlar.${davet.status}`, { defaultValue: davet.status })}</Badge>}
              <div className="small text-muted mt-1">
                {davet.sent_at && <div>{t('davet.gonderilme', { zaman: tarih(davet.sent_at) })}</div>}
                {davet.expires_at && davet.status !== 'NONE' && <div>{t('davet.sonGecerlilik', { zaman: tarih(davet.expires_at) })}</div>}
                {davet.last_used_at && <div>{t('davet.sonKullanim', { zaman: tarih(davet.last_used_at) })}</div>}
              </div>
              {davet.status === 'LOCKED' && <div className="small text-danger mt-1">{t('davet.kilitliNotu')}</div>}
            </div>

            {adim === 'onay' && (
              <Alert color="info" fade={false} data-testid="panel-daveti-onay">
                {t('davet.onay', { numara: davet.phone_masked || '' })}
                <div className="small mt-1">{t('davet.onayNotu')}</div>
              </Alert>
            )}
            {adim === 'iptal' && (
              <Alert color="warning" fade={false} data-testid="panel-daveti-iptal-onay">{t('davet.iptalSorusu')}</Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={calisiyor}>{t('kapat')}</Button>
        {davet && adim === 'ozet' && iptalde && (
          <Button color="danger" outline id="panel-daveti-iptal" disabled={calisiyor} onClick={() => setAdim('iptal')}>{t('davet.iptalEt')}</Button>
        )}
        {davet && adim === 'ozet' && (gonderilebilir || tekrar) && (
          <Button color="primary" id="panel-daveti-gonder" disabled={calisiyor || kalan > 0} onClick={() => setAdim('onay')}>
            {kalan > 0 ? t('davet.yenidenBekle', { saniye: kalan }) : tekrar ? t('davet.yenidenGonder') : t('davet.gonder')}
          </Button>
        )}
        {adim === 'onay' && (
          <>
            <Button color="light" disabled={calisiyor} onClick={() => setAdim('ozet')}>{t('vazgec')}</Button>
            <Button color="primary" id="panel-daveti-gonder-onayla" disabled={calisiyor} onClick={gonder}>{calisiyor ? <Spinner size="sm" /> : t('davet.smsGonder')}</Button>
          </>
        )}
        {adim === 'iptal' && (
          <>
            <Button color="light" disabled={calisiyor} onClick={() => setAdim('ozet')}>{t('vazgec')}</Button>
            <Button color="danger" id="panel-daveti-iptal-onayla" disabled={calisiyor} onClick={iptalEt}>{calisiyor ? <Spinner size="sm" /> : t('davet.iptalEt')}</Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

/** Küçük "Panele davet et" düğmesi + penceresi; kart eylem sütunlarına ve künye başlığına konur. */
export function PanelDavetiDugmesi({ personId, adSoyad, className = '', outline = true }) {
  const { t } = useTranslation('sms');
  const [acik, setAcik] = useState(false);
  if (!personId) return null;
  return (
    <>
      <Button color="primary" outline={outline} size="sm" className={className} id={`panel-daveti-ac-${personId}`} onClick={() => setAcik(true)}>
        <UserPlus size={14} className="me-1" />{t('davet.dugme')}
      </Button>
      {acik && <PanelDavetiModal personId={personId} adSoyad={adSoyad} onClose={() => setAcik(false)} />}
    </>
  );
}
